// src/economy/WalletContext.js — Kristal / Oyuntu Puan durumu, günlük hediye, görevler, etkinlikler, hediyeler
import React, { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { COST_LOCAL, COST_ONLINE, DAILY_KRISTAL, dayNo } from './config';

const WalletModal = lazy(() => import('./WalletModal'));
const Ctx = createContext(null);
const ops = () => import('./wallet');

/** Misafir adı: online lobide yazılan ad ya da Misafir-XXXX */
export function guestName(uid) {
  try {
    const n = localStorage.getItem('oy-online-name');
    if (n && n.trim().length >= 2) return n.trim().slice(0, 20);
  } catch {
    /* depolama kapalı */
  }
  return `Misafir-${(uid || '0000').slice(0, 4).toUpperCase()}`;
}

export function WalletProvider({ children }) {
  const { uid, user, userData } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [quests, setQuests] = useState([]);
  const [events, setEvents] = useState([]);
  const [ban, setBan] = useState(null);
  const [modal, setModal] = useState(null); // { tab, notice }
  const [toasts, setToasts] = useState([]);
  const dailyRef = useRef(null);
  const name = userData?.nickname || (user && user.displayName) || guestName(uid);

  const toast = useCallback((text, kind = 'info') => {
    const id = Math.random().toString(36).slice(2);
    setToasts((l) => [...l.slice(-2), { id, text, kind }]);
    setTimeout(() => setToasts((l) => l.filter((t) => t.id !== id)), 4200);
  }, []);

  // Cüzdan ve hediyeler
  useEffect(() => {
    setWallet(null);
    setBan(null);
    if (!uid) return undefined;
    let offW = () => {};
    let offG = () => {};
    let alive = true;
    ops().then(async (w) => {
      if (!alive) return;
      offW = await w.watchWallet(uid, name, (data) => alive && setWallet(data), (e) => console.warn('Cüzdan:', e?.code || e));
      offG = await w.watchGifts(uid, (g) => toast(`🎁 Hediye: +${g.amount} ${g.currency === 'puan' ? 'Oyuntu Puan' : 'Kristal'}${g.from === 'admin' ? ' (Oyuntu)' : g.fromName ? ` · ${g.fromName}` : ''}${g.note ? ` — ${g.note}` : ''}`, 'gift'));
      setBan(await w.checkBan(uid));
      if (!alive) {
        offW();
        offG();
      }
    });
    return () => {
      alive = false;
      offW();
      offG();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Günlük hediye ve görev günü
  useEffect(() => {
    if (!uid || !wallet) return;
    const today = dayNo();
    if (wallet.fresh && dailyRef.current !== `${uid}-${today}`) {
      dailyRef.current = `${uid}-${today}`;
      toast(`💎 Hoş geldin! ${DAILY_KRISTAL} Kristal hediye`, 'gift');
    } else if ((wallet.dailyDay || 0) < today && dailyRef.current !== `${uid}-${today}`) {
      dailyRef.current = `${uid}-${today}`;
      ops().then((w) => w.claimDaily(uid)).then((ok) => ok && toast(`💎 Günlük hediye: +${DAILY_KRISTAL} Kristal`, 'gift'));
    }
    if (wallet.qDay !== today) ops().then((w) => w.resetQuestDay(uid));
  }, [uid, wallet, toast]);

  // Görevler ve etkinlikler (günde bir yüklenir)
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      ops().then(async (w) => {
        const [q, e] = await Promise.all([w.loadQuests().catch(() => []), w.loadEvents().catch(() => [])]);
        if (alive) {
          setQuests(q);
          setEvents(e);
        }
      });
    }, 1500);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [uid]);

  const openWallet = useCallback((tab = 'market', notice = '') => setModal({ tab, notice }), []);

  /** Oyun başlatma ücreti. Yetersizse market açılır ve false döner. */
  const payForGame = useCallback(
    async (online) => {
      const cost = online ? COST_ONLINE : COST_LOCAL;
      if (!uid || !wallet) return true; // cüzdan henüz yok (bağlantı yok): oyunu engelleme
      if (wallet.kristal < cost) {
        openWallet('market', `Bu oyun için ${cost} Kristal gerekiyor. Oyuntu Puanlarını Kristal'e çevirebilir ya da yarınki günlük hediyeyi bekleyebilirsin.`);
        return false;
      }
      const w = await ops();
      const ok = await w.spendKristal(uid, cost);
      if (!ok) openWallet('market', 'Kristal harcanamadı. Lütfen tekrar deneyin.');
      return ok;
    },
    [uid, wallet, openWallet]
  );

  /** Oyun sonucu ödülü ve görev ilerlemesi */
  const rewardGame = useCallback(
    async ({ gameId, won, online }) => {
      if (!uid || !wallet) return 0;
      const w = await ops();
      const p = await w.earnFromGame(uid, { gameId, won, online, quests, events });
      if (p) toast(`⭐ +${p} Oyuntu Puan`, 'puan');
      return p;
    },
    [uid, wallet, quests, events, toast]
  );

  const value = useMemo(
    () => ({ uid, wallet, quests, events, ban, name, registered: !!user, openWallet, payForGame, rewardGame, toast }),
    [uid, wallet, quests, events, ban, name, user, openWallet, payForGame, rewardGame, toast]
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      {modal && (
        <Suspense fallback={null}>
          <WalletModal tab={modal.tab} notice={modal.notice} onClose={() => setModal(null)} />
        </Suspense>
      )}
      {toasts.length > 0 && (
        <div className="wl-toasts" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className={`wl-toast is-${t.kind}`}>{t.text}</div>
          ))}
        </div>
      )}
    </Ctx.Provider>
  );
}

const EMPTY = { wallet: null, quests: [], events: [], openWallet: () => {}, payForGame: async () => true, rewardGame: async () => 0, toast: () => {} };
export const useWallet = () => useContext(Ctx) || EMPTY;

/** Header'daki Kristal / Puan göstergesi */
export function WalletChip() {
  const { wallet, openWallet, quests } = useWallet();
  const k = wallet ? wallet.kristal : '–';
  const p = wallet ? wallet.puan : '–';
  const claimable = wallet ? quests.filter((q) => (wallet.qDone?.[q.id] || 0) >= q.target && !(wallet.qClaimed || []).includes(q.id)).length : 0;
  return (
    <button type="button" className="wl-chip" onClick={() => openWallet(claimable ? 'quests' : 'market')} aria-label={`Kristal ${k}, Oyuntu Puan ${p}. Market ve görevler`}>
      <span className="wl-k" title="Kristal">💎 {k}</span>
      <span className="wl-p" title="Oyuntu Puan">⭐ {p}</span>
      {claimable > 0 && <span className="wl-dot" aria-hidden="true">{claimable}</span>}
    </button>
  );
}
